import type { PartialGenerationBrief } from '@/features/ara-generation/types/generation-brief'

/**
 * ARA SSE event contract — mirrors master's `apps/ara/app/views/events.py` +
 * `apps/ara/app/services/ara_service.py` edit stream as the source of truth.
 *
 * Streams the front consumes against master:
 *  - generation (`POST /ara/stream`): init -> node* -> section* -> role* -> done | error
 *  - edit chat  (`POST /ara/edit/stream`): text* -> edit* / target_edit* / chart_edit* / remove / clarify -> done | error
 *
 * Intake frames (`text_delta` / `brief` / `brief_partial`) remain modelled for
 * preserved client modules that are currently unmounted (no master endpoint).
 * `span_edit` is kept in the edit union for the client span path; master may not
 * emit it yet — the parser skips truly unknown types.
 *
 * `section.md` is a deprecated alias of `html` (the backend still emits both).
 * Meta edits arrive on the wire as `edit` with `section_key: "meta"` and JSON
 * inside `html` (often wrapped by `section_html`); the SSE parser normalizes
 * that into `{ patch }` for consumers. `node` is optional on generation errors.
 */

export type AraNodeStatus = 'complete' | 'error' | string

export type AraBackboneStage = {
  node: string
  label: string
  order: number
}

export type AraSectionDescriptor = {
  id: string
  label: string
  shared: boolean
}

export type AraRoleDescriptor = {
  id: string
  label: string
}

export type AraInitFrame = {
  type: 'init'
  backbone: AraBackboneStage[]
  sections: AraSectionDescriptor[]
  roles: AraRoleDescriptor[]
}

export type AraNodeFrame = {
  type: 'node'
  node: string
  label: string
  order: number
  status: AraNodeStatus
  elapsed_ms: number
  meta: Record<string, unknown>
  detail: Record<string, unknown>
}

export type AraSectionFrame = {
  type: 'section'
  key: string
  label: string
  status: AraNodeStatus
  elapsed_ms: number
  chars: number
  html: string
  /** @deprecated back-compat alias of `html`; the backend still emits it. */
  md?: string
}

export type AraRoleFrame = {
  type: 'role'
  role: string
  label: string
  sections: unknown[]
  html: string
  status: AraNodeStatus
  elapsed_ms: number
}

export type AraDoneFrame = {
  type: 'done'
  structured_ara: Record<string, unknown>
  roles: string[]
  error: string | null
}

export type AraErrorFrame = {
  type: 'error'
  detail: string
  node?: string
}

/** A section edit emitted by the edit-chat stream. Empty HTML removes the whole section, title included.
 * A frame for a section key outside the request's sections dict is a chat-inserted CUSTOM
 * section: `custom` marks it, `after` echoes the anchor target id the model named and
 * `after_section` is the backend-resolved section that anchor lives in (null = append at
 * the end) — the frontend inserts the key into the section order on accept. */
export type AraEditSectionFrame = {
  type: 'edit'
  section_key: string
  html: string
  custom?: boolean
  after?: string | null
  after_section?: string | null
}

/**
 * A meta edit emitted by the edit-chat stream when `section_key === "meta"`.
 * The backend wraps a JSON object (a patch over hero/header fields) in the
 * same `edit` envelope, but the payload is JSON — not HTML — so it gets a
 * distinct shape. Valid patch fields: name, company_name, deal_owner_name,
 * se_name, contracted_smart_tracs.
 *
 * `sector` is deliberately NOT one of them. It is not a header label: it
 * selects the peer cohort behind every failure probability, benchmark and
 * comparable-customer figure in the report, and a meta patch cannot re-run
 * those queries. Accepting one relabelled the page while the numbers stayed on
 * the old industry (SCP-112). Changing it requires regeneration.
 */
export type AraEditMetaPatch = {
  name?: string
  company_name?: string
  deal_owner_name?: string
  se_name?: string
  contracted_smart_tracs?: string | number
}

/**
 * A meta patch as it arrives on the wire, before `sector` is stripped.
 *
 * Separate from `AraEditMetaPatch` so the stripping is visible in the types: the
 * model can still emit a sector despite the prompt forbidding it, and pretending
 * otherwise would make the parser's guard look like dead code.
 */
export type AraIncomingEditMetaPatch = AraEditMetaPatch & { sector?: string }

export type AraEditMetaFrame = {
  type: 'edit'
  section_key: 'meta'
  patch: AraEditMetaPatch
}

/**
 * An element-scoped edit emitted when the user targets a single element rather
 * than a whole section. The backend replaces the tagged element's `outer_html`
 * with `html`, keyed by the element's `target_key`. An empty replacement for a
 * section or section-body target removes the complete section, title included.
 * Mirrors `apps/ara/app/services/ara_service.py` (`target_edit` frame).
 */
export type AraTargetEditFrame = {
  type: 'target_edit'
  target_key: string
  html: string
  /**
   * The pipeline's authoritative resolved target, attached by the backend
   * whenever the request was narrowed (picker target or resolver hit). The
   * model's `target_key` echo is unreliable for no-selection requests —
   * splice by this identity instead (FE-6070).
   */
  target?: AraEditTarget
}

/**
 * A targeted chart config replacement emitted when the user edits a chart. The
 * backend JSON-parses the tagged block into a `patch` over the chart's config,
 * keyed by `chart_key`. Collecting a frame requires a plain non-empty object
 * `patch` and a resolvable chart id (`chart_key` → target id → target key);
 * the stored override is a full Chart.js config replacement applied through
 * `/ara/render`, keyed by the rendered canvas element id.
 * Mirrors `apps/ara/app/services/ara_service.py` (`chart_edit` frame).
 */
export type AraChartEditFrame = {
  type: 'chart_edit'
  chart_key: string
  patch: Record<string, unknown>
}

/**
 * A surgical span replacement emitted when the user edits a sub-range inside a
 * single text node. The backend returns only the replacement text for the span
 * (no HTML), keyed by the enclosing leaf's `target_key`; the frontend reinserts
 * it at the captured offsets as a pure string splice (`ara-span-edit`).
 * Mirrors `apps/ara/app/services/ara_service.py` (`span_edit` frame).
 */
export type AraSpanEditFrame = {
  type: 'span_edit'
  target_key: string
  text: string
}

export type AraEditFrame = AraEditSectionFrame | AraEditMetaFrame

/**
 * Metadata for the element the user scoped an edit to, sent as the request
 * `target` field (wired in slice 10). Mirrors the backend `target` dict — the
 * frontend injected-iframe script produces `chart_spec` for chart targets and
 * `mermaid_source` for mermaid diagrams; both are absent for plain elements.
 */
export type AraEditTarget = {
  key: string
  selector: string
  sectionKey: string
  id: string
  type: string
  outer_html: string
  /** Edit-group name when the target is a component (`data-edit-group`). */
  group?: string
  /** Synthetic column hint when the picker selected a table column
   * (`data-edit-col`); the target itself is the enclosing group. */
  column?: string
  chart_spec?: Record<string, unknown>
  mermaid_source?: string
  /** Set on a clarify resubmit: the backend applies this index id directly
   * (`group:<section>:<n>` / `column:…` / `key:…` / `section:<key>`) without
   * running the resolver again (FE-6079). */
  resolved_id?: string
  /** Surgical span offsets, relative to the enclosing leaf's `textContent`, and
   * the exact selected text. Present only for `type: 'span'` targets. */
  span_start?: number
  span_end?: number
  span_text?: string
}

export type AraTextFrame = {
  type: 'text'
  content: string
}

export type AraTextDeltaFrame = {
  type: 'text_delta'
  content: string
}

export type AraBriefFrame = {
  type: 'brief'
  brief: PartialGenerationBrief
}

export type AraBriefPartialFrame = {
  type: 'brief_partial'
  brief: PartialGenerationBrief
}

/**
 * A deterministic removal ask from the report iframe (FE-6175): a target
 * reference resolved against the stored section HTML. A column target
 * (`group` plus `column`) runs the column op; anything else runs the element
 * op. Unresolvable targets change no state.
 */
export type AraRemoveIntent = {
  sectionKey?: string
  wholeSection?: boolean
  group?: string
  column?: string
  columnLabel?: string
  key?: string
  selector?: string
  id?: string
}

export type AraEditDoneFrame = {
  type: 'done'
}

export type AraRemoveFrame = {
  type: 'remove'
  op: 'remove'
  target: string
  target_type: string
  label: string
  section_key: string
  section_label: string
  parent_label: string
  group: string
  column: string
  key: string
}

/**
 * An ambiguity answer emitted when a no-selection request matches several
 * addressable targets equally. The stream ends after `done` with no proposals;
 * the user picks one option and the frontend resubmits the same request with
 * `target.resolved_id`, which the backend applies without consulting the
 * resolver again. Mirrors `apps/ara/app/services/ara_service.py` (`clarify`
 * frame, FE-6079).
 */
export type AraClarifyOption = {
  id: string
  type: string
  label: string
  section_key: string
}

export type AraClarifyFrame = {
  type: 'clarify'
  request: string
  options: AraClarifyOption[]
}

export type AraEditErrorFrame = {
  type: 'error'
  detail: string
}

export type AraIntakeDoneFrame = {
  type: 'done'
}

export type AraIntakeErrorFrame = {
  type: 'error'
  detail: string
}

/** Generation-stream frames (`/ara/stream`). */
export type AraGenerationEvent =
  | AraInitFrame
  | AraNodeFrame
  | AraSectionFrame
  | AraRoleFrame
  | AraDoneFrame
  | AraErrorFrame

/**
 * A sector correction, which is not an edit: nothing in the current document
 * changes. The sector selects the peer cohort every failure probability and
 * benchmark was computed against, and the edit service cannot recompute one, so
 * it asks the client to start a fresh generation with this sector pinned.
 *
 * `sector` is already validated against the moat vocabulary server-side — the
 * backend drops the frame rather than emitting a value nothing can filter on.
 * Acting on it discards the current report, so it is only emitted when the user
 * explicitly asked to change the sector.
 */
export type AraRegenerateFrame = {
  type: 'regenerate'
  sector: string
}

/**
 * Candidate cohorts for the SE to choose between, offered before anything is
 * rebuilt. Mapping an SE's words onto the warehouse vocabulary is a judgement
 * call with no exact answer ("medical devices" lands on Pharmaceutical), and
 * they know the plant better than the model does, so the first ask proposes and
 * the rebuild waits for a click.
 *
 * Best fit first. Every value is validated against the moat vocabulary
 * server-side, so anything here is safe to put on a button.
 */
export type AraSectorOptionsFrame = {
  type: 'sector_options'
  sectors: string[]
}

/**
 * A scope redirect, which is not an edit: which assets the report covers is a
 * generation input chosen in the Scope panel, and the edit service holds neither
 * the asset tree nor a way to recompute a scoped report's coverage figures. The
 * client renders a button that opens the panel. No edit frame is emitted in the
 * same turn, so the report HTML is guaranteed untouched (SCP-130).
 */
export type AraScopeRedirectFrame = {
  type: 'scope_redirect'
}

/** Edit-chat-stream frames (`/ara/edit/stream`). */
export type AraEditChatEvent =
  | AraTextFrame
  | AraEditFrame
  | AraTargetEditFrame
  | AraChartEditFrame
  | AraSpanEditFrame
  | AraRegenerateFrame
  | AraSectorOptionsFrame
  | AraScopeRedirectFrame
  | AraRemoveFrame
  | AraClarifyFrame
  | AraEditDoneFrame
  | AraEditErrorFrame

/** Intake-stream frames (`/ara/intake/stream`). */
export type AraIntakeEvent =
  | AraTextFrame
  | AraTextDeltaFrame
  | AraBriefFrame
  | AraBriefPartialFrame
  | AraIntakeDoneFrame
  | AraIntakeErrorFrame

/** The full discriminated union consumed by every ARA phase. */
export type AraStreamEvent = AraGenerationEvent | AraEditChatEvent | AraIntakeEvent

export const ARA_GENERATION_FRAME_TYPES = [
  'init',
  'node',
  'section',
  'role',
  'done',
  'error'
] as const

export const ARA_EDIT_CHAT_FRAME_TYPES = [
  'text',
  'edit',
  'target_edit',
  'chart_edit',
  'span_edit',
  'regenerate',
  'sector_options',
  'scope_redirect',
  'remove',
  'clarify',
  'done',
  'error'
] as const

export const ARA_INTAKE_FRAME_TYPES = [
  'text',
  'text_delta',
  'brief',
  'brief_partial',
  'done',
  'error'
] as const

export const ARA_META_SECTION_KEY = 'meta' as const

/** Prefix the ARA edit normalizer puts on stable edit keys it assigns to text
 * leaves of chat-inserted fragments (`chat.<hash>.<index>`). Edits to these
 * keys write into the document HTML (sections), never structuredAra. */
export const ARA_CHAT_EDIT_KEY_PREFIX = 'chat.' as const

export type AraFrameType =
  | (typeof ARA_GENERATION_FRAME_TYPES)[number]
  | 'text'
  | 'text_delta'
  | 'brief'
  | 'brief_partial'
  | 'edit'
  | 'target_edit'
  | 'chart_edit'
  | 'span_edit'
  | 'remove'
